import {
  faArrowRightLong,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Divider,
  Flex,
  LoadingOverlay,
  Skeleton,
  Text,
  Tooltip,
} from '@mantine/core'

import { useQueryGetBookingById } from '@flecto/core/saas'
import { useQueryGetUserSubscription } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import {
  type CoreBookingStatus,
  type ParameterID,
  type CoreInsuranceResponseList,
  type CoreInsuranceQuoteResponseList,
} from '@flecto/types'
import {
  Badge,
  CardList,
  type CardListProps,
  Icon,
  TextCurrency,
  TextDate,
} from '@flecto/ui'

export type CardBookingQuickDetailsProps = Omit<CardListProps, 'children'> & {
  bookingId?: ParameterID
  previewNewBookingStatus?: CoreBookingStatus
  previewNewStartAt?: string
  previewNewEndAt?: string
  withDates?: boolean
  insurances?: CoreInsuranceResponseList
  quotes?: CoreInsuranceQuoteResponseList
  isLoadingQuotes?: boolean
}

export function CardBookingQuickDetails({
  bookingId,
  previewNewBookingStatus,
  previewNewStartAt,
  previewNewEndAt,
  withDates,
  insurances,
  quotes,
  isLoadingQuotes,
  ...props
}: CardBookingQuickDetailsProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'bookings' })
  const { t: tInsurance } = useTranslation('saas', {
    keyPrefix: 'booking_dates',
  })

  const { data: booking, isLoading } = useQueryGetBookingById({ bookingId })

  const { data: userSubscription } = useQueryGetUserSubscription()

  return (
    <CardList {...props}>
      {isLoading || (isLoadingQuotes && <LoadingOverlay visible mih={100} />)}
      <CardList.Item>
        <CardList.ItemLabel>
          {t('card_quick_details_booking_id')}
        </CardList.ItemLabel>
        <CardList.ItemValue>
          <Text>#{booking?.internal_id}</Text>
        </CardList.ItemValue>
      </CardList.Item>

      {withDates && (
        <>
          <CardList.Item>
            <CardList.ItemLabel>
              {t('card_quick_details_start_at')}
            </CardList.ItemLabel>
            <CardList.ItemValue>
              <TextDate
                date={booking?.start_at ?? ''}
                td={
                  previewNewStartAt && previewNewStartAt !== booking?.start_at
                    ? 'line-through'
                    : undefined
                }
                c={
                  previewNewStartAt && previewNewStartAt !== booking?.start_at
                    ? 'dimmed'
                    : 'black'
                }
                variant='datetime-brief'
              />
              {previewNewStartAt && previewNewStartAt !== booking?.start_at && (
                <>
                  <Icon icon={faArrowRightLong} />
                  <TextDate date={previewNewStartAt} variant='datetime-brief' />
                </>
              )}
            </CardList.ItemValue>
          </CardList.Item>

          <CardList.Item>
            <CardList.ItemLabel>
              {t('card_quick_details_end_at')}
            </CardList.ItemLabel>
            <CardList.ItemValue>
              <TextDate
                date={booking?.end_at ?? ''}
                td={
                  previewNewEndAt && previewNewEndAt !== booking?.end_at
                    ? 'line-through'
                    : undefined
                }
                c={
                  previewNewEndAt && previewNewEndAt !== booking?.end_at
                    ? 'dimmed'
                    : 'black'
                }
                variant='datetime-brief'
              />
              {previewNewEndAt && previewNewEndAt !== booking?.end_at && (
                <>
                  <Icon icon={faArrowRightLong} />
                  <TextDate
                    date={previewNewEndAt}
                    variant='datetime-brief'
                    c='orange.7'
                  />
                </>
              )}
            </CardList.ItemValue>
          </CardList.Item>

          {userSubscription?.plan !== 'Starter' &&
            booking?.meta?.has_issued_insurances && (
              <>
                <Divider color='gray.3' />
                <Flex gap='md' p='md' className=''>
                  <CardList.ItemLabel>
                    {booking?.insured
                      ? tInsurance('insurance_total')
                      : tInsurance('insurance')}
                    <Tooltip
                      label={tInsurance('insurance_total_tooltip')}
                      zIndex={99999}
                      withinPortal
                    >
                      <Box display='inline' ml='xs'>
                        <Icon icon={faInfoCircle} />
                      </Box>
                    </Tooltip>
                  </CardList.ItemLabel>
                  <CardList.ItemValue>
                    {booking?.insured ? (
                      isLoadingQuotes ? (
                        <Skeleton width={100} height={20} />
                      ) : (
                        <>
                          <TextCurrency
                            cents={quotes?.current_total_cents ?? 0}
                            c='dimmed'
                            td='line-through'
                          />
                          <Icon icon={faArrowRightLong} />
                          <TextCurrency
                            cents={quotes?.total_cents ?? 0}
                            c='orange.7'
                          />
                        </>
                      )
                    ) : (
                      <Badge color='red'>
                        {tInsurance('insurance_disabled')}
                      </Badge>
                    )}
                  </CardList.ItemValue>
                </Flex>
              </>
            )}
        </>
      )}
    </CardList>
  )
}
